import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { MedicalServicesOutlined as ServiceIcon } from "@mui/icons-material";
import { DrawerField } from "../../molecules";
import { FormCheckbox } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import { kurusToInput, parseMoneyInput } from "../../../utils/money";
import { DrawerInput, FormDrawer, fieldGridSx } from "./billingParts";

const EMPTY = { name: "", durationMin: "30", price: "", note: "", isActive: true };

/**
 * Hizmet oluşturma / düzenleme drawer'ı. Fiyat TL girilir, kuruş gönderilir.
 * @param {object|null} service - düzenlenen hizmet (null → yeni)
 * @param {(payload) => Promise} onSave
 */
export default function ServiceEditorDrawer({ open, onClose, service, onSave, saving }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(
      service
        ? {
            name: service.name || "",
            durationMin: String(service.durationMin || ""),
            price: kurusToInput(service.price),
            note: service.note || "",
            isActive: service.isActive !== false,
          }
        : EMPTY
    );
  }, [open, service]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const price = parseMoneyInput(form.price);
  const duration = Number(form.durationMin);
  const durationOk = Number.isInteger(duration) && duration >= 5 && duration <= 600;
  const valid = form.name.trim() && durationOk && price !== null;

  const submit = () =>
    valid &&
    onSave({
      ...(service ? { serviceId: service._id } : {}),
      name: form.name.trim(),
      durationMin: duration,
      price,
      note: form.note.trim(),
      ...(service ? { isActive: form.isActive } : {}),
    });

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      icon={<ServiceIcon />}
      title={service ? "Hizmeti düzenle" : "Yeni hizmet"}
      subtitle={`Randevuda seçilir; süre bitiş saatini, fiyat ${terms.patientLower} hesabını belirler.`}
      onSubmit={submit}
      submitLabel={service ? "Kaydet" : "Ekle"}
      loading={saving}
      disabled={!valid}
    >
      <Box sx={fieldGridSx}>
        <DrawerField label="Hizmet adı *" fullWidth>
          <DrawerInput
            palette={palette}
            autoFocus
            placeholder="Ör. Hacamat, Diyet danışmanlığı"
            value={form.name}
            onChange={set("name")}
            inputProps={{ maxLength: 120, "aria-label": "Hizmet adı" }}
          />
        </DrawerField>
        <DrawerField
          label="Süre (dakika) *"
          hint={form.durationMin && !durationOk ? "5 ile 600 dakika arasında olmalı." : undefined}
        >
          <DrawerInput
            palette={palette}
            type="number"
            value={form.durationMin}
            onChange={set("durationMin")}
            inputProps={{ min: 5, max: 600, step: 5, "aria-label": "Süre (dakika)" }}
          />
        </DrawerField>
        <DrawerField
          label="Fiyat (₺) *"
          hint={form.price && price === null ? "Geçerli bir tutar girin (ör. 1.250,00)." : "Ücretsizse 0 yazın."}
        >
          <DrawerInput
            palette={palette}
            placeholder="0,00"
            value={form.price}
            onChange={set("price")}
            inputProps={{ inputMode: "decimal", "aria-label": "Fiyat" }}
          />
        </DrawerField>
        <DrawerField label="Not" fullWidth>
          <DrawerInput
            palette={palette}
            multiline
            minRows={2}
            placeholder="İsteğe bağlı açıklama"
            value={form.note}
            onChange={set("note")}
            inputProps={{ maxLength: 500, "aria-label": "Not" }}
          />
        </DrawerField>
        {service ? (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <FormCheckbox
              palette={palette}
              label="Kullanımda (kapalıysa randevuda seçilemez; geçmiş kayıtlar korunur)"
              checked={form.isActive}
              onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
            />
          </Box>
        ) : null}
      </Box>
    </FormDrawer>
  );
}
